/**
 * UpsOutletsPanel
 * Tomas de salida del SAI (UPS) y conexiones de alimentación.
 *
 * Muestra cada toma como una tarjeta y permite:
 *  - conectarla a una PDU (queda como fuente aguas arriba de la regleta),
 *  - conectarla a un servidor, switch, cabina, chasis, puesto…
 *  - editar sus propiedades (etiqueta, tipo, fase, circuito, amperaje),
 *  - desconectar lo que cuelga de ella.
 *
 * Las tomas se generan automáticamente al guardar el SAI con un
 * `outlets_count`; el backend las sincroniza (nunca borra una toma en uso).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import Dialog from './common/Dialog';

// ── Catálogos ────────────────────────────────────────────────────────────────

const OUTLET_TYPES = [
  { value: '', label: '— Sin especificar —' },
  { value: 'C13', label: 'IEC C13 (10 A)' },
  { value: 'C19', label: 'IEC C19 (16 A)' },
  { value: 'Schuko', label: 'Schuko' },
  { value: 'UK', label: 'UK' },
  { value: 'IEC309', label: 'IEC 309' },
];

const CABLE_TYPES = [
  { value: '', label: '— Sin especificar —' },
  { value: 'IEC C13/C14', label: 'IEC C13/C14' },
  { value: 'IEC C19/C20', label: 'IEC C19/C20' },
  { value: 'Schuko', label: 'Schuko' },
  { value: 'CEE 7/7', label: 'CEE 7/7' },
  { value: 'Otro', label: 'Otro' },
];

const CABLE_COLORS = [
  { value: '', label: '— Sin especificar —' },
  { value: 'negro', label: 'Negro' },
  { value: 'rojo', label: 'Rojo' },
  { value: 'azul', label: 'Azul' },
  { value: 'verde', label: 'Verde' },
  { value: 'amarillo', label: 'Amarillo' },
  { value: 'blanco', label: 'Blanco' },
  { value: 'gris', label: 'Gris' },
  { value: 'naranja', label: 'Naranja' },
  { value: 'morado', label: 'Morado' },
  { value: 'rosa', label: 'Rosa' },
];

const CABLE_COLOR_HEX = {
  negro: '#475569', rojo: '#ef4444', azul: '#3b82f6', verde: '#22c55e',
  amarillo: '#eab308', blanco: '#e2e8f0', gris: '#94a3b8',
  naranja: '#f97316', morado: '#a855f7', rosa: '#ec4899',
};

function cableHex(color) {
  if (!color) return '#6366f1';
  return CABLE_COLOR_HEX[String(color).toLowerCase()] || '#6366f1';
}

// ── Agrupación de equipos de destino ─────────────────────────────────────────

const CONSUMER_GROUPS = [
  { key: 'server', label: 'Servidores' },
  { key: 'pdu', label: 'PDUs' },
  { key: 'switch', label: 'Switches' },
  { key: 'storage', label: 'Cabinas de almacenamiento' },
  { key: 'router', label: 'Routers' },
  { key: 'firewall', label: 'Firewalls' },
  { key: 'chassis', label: 'Chasis' },
  { key: 'workstation', label: 'Puestos de trabajo' },
  { key: 'other', label: 'Otros elementos' },
];

function consumerGroup(consumer) {
  if (consumer.consumer_type === 'chassis') return 'chassis';
  if (consumer.consumer_type === 'workstation') return 'workstation';
  const type = consumer.element_type || '';
  if (['server', 'pdu', 'switch', 'storage', 'router', 'firewall'].includes(type)) {
    return type;
  }
  return 'other';
}

// ── Tarjeta de toma ──────────────────────────────────────────────────────────

function OutletCard({ outlet, onOpen }) {
  const connection = outlet.connection || null;
  const consumerName =
    outlet.consumer_name || outlet.child_pdu_name || connection?.consumer_name || null;
  const isUsed = Boolean(consumerName);
  const isPduChild = Boolean(outlet.child_pdu_name);

  return (
    <button
      type="button"
      onClick={() => onOpen(outlet)}
      title={isUsed ? `Conectado a: ${consumerName}` : 'Toma libre — pulsa para conectar'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 3,
        padding: '0.55rem 0.65rem',
        border: `2px solid ${isUsed ? (isPduChild ? '#f59e0b' : '#4ade80') : 'var(--border)'}`,
        borderRadius: 8,
        background: isUsed
          ? (isPduChild ? 'rgba(245,158,11,0.08)' : 'rgba(74,222,128,0.08)')
          : 'var(--bg-surface)',
        cursor: 'pointer',
        minWidth: 132,
        maxWidth: 190,
        flex: '1 0 132px',
        textAlign: 'left',
        color: 'inherit',
        font: 'inherit',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
        <span style={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: isUsed ? (isPduChild ? '#d97706' : '#16a34a') : 'var(--text-muted)',
          letterSpacing: 0.4,
        }}>
          #{outlet.outlet_number}
        </span>
        <span style={{ fontSize: '0.95rem', lineHeight: 1 }}>
          {isPduChild ? '🔌' : (isUsed ? '⚡' : '⬜')}
        </span>
        <span style={{
          marginLeft: 'auto',
          fontSize: '0.66rem',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}>
          {outlet.outlet_type || '—'}
        </span>
      </span>
      <span style={{
        fontSize: '0.78rem',
        fontWeight: 600,
        color: 'var(--text-primary)',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {outlet.label || `Toma ${outlet.outlet_number}`}
      </span>
      <span style={{
        fontSize: '0.72rem',
        color: isUsed ? (isPduChild ? '#d97706' : '#16a34a') : 'var(--text-muted)',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontStyle: isUsed ? 'normal' : 'italic',
      }}>
        {isUsed ? `→ ${consumerName}` : 'Libre'}
      </span>
      {connection?.psu_label && (
        <span style={{ fontSize: '0.66rem', color: 'var(--text-muted)' }}>
          {connection.psu_label}
          {connection.psu_component_model ? ` · ${connection.psu_component_model}` : ''}
        </span>
      )}
    </button>
  );
}

// ── Diagrama de conexiones ───────────────────────────────────────────────────

function ConnectionDiagram({ outlets }) {
  const connected = outlets.filter(
    o => o.consumer_name || o.child_pdu_name || o.connection
  );
  if (!connected.length) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', fontStyle: 'italic', padding: '0.75rem 0' }}>
        Sin equipos conectados a las tomas del SAI.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', padding: '0.5rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ minWidth: 120, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', textAlign: 'center' }}>
          Toma del SAI
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ minWidth: 170, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', textAlign: 'center' }}>
          Equipo alimentado
        </div>
      </div>
      {connected.map(outlet => {
        const connection = outlet.connection || {};
        const isPduChild = Boolean(outlet.child_pdu_name);
        const color = isPduChild ? '#f97316' : cableHex(connection.cable_color);
        const consumerName =
          outlet.child_pdu_name || outlet.consumer_name || connection.consumer_name || '?';
        return (
          <div key={outlet.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 6,
              padding: '0.3rem 0.6rem',
              fontSize: '0.78rem',
              color: '#d97706',
              fontWeight: 600,
              minWidth: 120,
              textAlign: 'center',
            }}>
              <code style={{ background: 'rgba(245,158,11,0.16)', padding: '1px 5px', borderRadius: 3, color: '#b45309', fontSize: '0.72rem' }}>
                #{outlet.outlet_number}
              </code>
              {outlet.outlet_type && (
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 1 }}>
                  {outlet.outlet_type}
                </div>
              )}
            </div>
            <div style={{ flex: 1, position: 'relative', height: 28, display: 'flex', alignItems: 'center' }}>
              <div style={{ height: 2, width: '100%', background: `linear-gradient(90deg, rgba(245,158,11,0.5), ${color}, rgba(99,102,241,0.5))`, borderRadius: 1 }} />
              {!isPduChild && (connection.cable_type || connection.cable_color) && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--bg-surface)',
                  border: `1px solid ${color}`,
                  borderRadius: 3,
                  padding: '1px 6px',
                  fontSize: '0.67rem',
                  color: 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                }}>
                  {[connection.cable_type, connection.cable_color].filter(Boolean).join(' · ')}
                </div>
              )}
              {isPduChild && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--bg-surface)',
                  border: '1px solid #f97316',
                  borderRadius: 3,
                  padding: '1px 6px',
                  fontSize: '0.67rem',
                  color: 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                }}>
                  entrada PDU
                </div>
              )}
            </div>
            <div style={{
              background: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.25)',
              borderRadius: 6,
              padding: '0.3rem 0.6rem',
              fontSize: '0.78rem',
              color: '#818cf8',
              fontWeight: 600,
              minWidth: 170,
              textAlign: 'center',
            }}>
              {consumerName}
              {!isPduChild && (connection.psu_label || connection.psu_component_model) && (
                <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 1 }}>
                  ⚡ {connection.psu_label || connection.psu_component_model}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Diálogo de toma ──────────────────────────────────────────────────────────

function OutletDialog({ outlet, upsId, companyId, consumers, onClose, onChanged }) {
  const connection = outlet.connection || null;
  const consumerName =
    outlet.consumer_name || outlet.child_pdu_name || connection?.consumer_name || null;
  const isPduChild = Boolean(outlet.child_pdu_name);
  const occupied = Boolean(consumerName);

  const [form, setForm] = useState({
    consumerKey: '',
    psu_component_id: '',
    psu_label: 'Entrada',
    cable_type: '',
    cable_color: '',
    notes: '',
  });
  const [propsForm, setPropsForm] = useState({
    label: outlet.label || '',
    outlet_type: outlet.outlet_type || '',
    phase: outlet.phase || '',
    circuit_id: outlet.circuit_id || '',
    voltage: outlet.voltage != null ? String(outlet.voltage) : '',
    amperage: outlet.amperage != null ? String(outlet.amperage) : '',
    is_active: outlet.is_active !== false,
    notes: outlet.notes || '',
  });
  const [psuOptions, setPsuOptions] = useState([]);
  const [usedPsuIds, setUsedPsuIds] = useState(() => new Set());
  const [loadingPsus, setLoadingPsus] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  const selectedConsumer = useMemo(
    () => consumers.find(c => `${c.consumer_type}:${c.id}` === form.consumerKey) || null,
    [consumers, form.consumerKey]
  );

  // PSUs del equipo elegido (si las tiene)
  useEffect(() => {
    if (!selectedConsumer) {
      setPsuOptions([]);
      setUsedPsuIds(new Set());
      return undefined;
    }
    if (selectedConsumer.consumer_type === 'workstation') {
      setPsuOptions([]);
      setUsedPsuIds(new Set());
      return undefined;
    }
    let cancelled = false;
    setLoadingPsus(true);
    api.getPowerConnections(
      selectedConsumer.consumer_type,
      selectedConsumer.id,
      companyId
    )
      .then(res => {
        if (!cancelled) {
          setUsedPsuIds(
            new Set((res.data || []).map(c => c.psu_component_id).filter(Boolean))
          );
        }
      })
      .catch(() => { if (!cancelled) setUsedPsuIds(new Set()); });
    const peticion = selectedConsumer.consumer_type === 'chassis'
      ? api.getChassisComponents(selectedConsumer.id, companyId)
      : api.getComponents(selectedConsumer.id, companyId);
    peticion
      .then(res => {
        if (!cancelled) {
          setPsuOptions((res.data || []).filter(c => c.component_type === 'psu'));
        }
      })
      .catch(() => { if (!cancelled) setPsuOptions([]); })
      .finally(() => { if (!cancelled) setLoadingPsus(false); });
    return () => { cancelled = true; };
  }, [selectedConsumer, companyId]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setProp = (key, value) => setPropsForm(f => ({ ...f, [key]: value }));

  const handleSelectConsumer = (key) => {
    const consumer = consumers.find(c => `${c.consumer_type}:${c.id}` === key) || null;
    setForm(f => ({
      ...f,
      consumerKey: key,
      psu_component_id: '',
      psu_label: consumer?.is_pdu ? 'Entrada PDU' : 'Entrada',
    }));
  };

  const handleConnect = async () => {
    if (!selectedConsumer) {
      setError('Elige el equipo que quieres alimentar desde esta toma');
      return;
    }
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      await api.connectUpsOutlet(upsId, outlet.id, {
        consumer_type: selectedConsumer.consumer_type,
        consumer_id: selectedConsumer.id,
        psu_component_id: form.psu_component_id ? Number(form.psu_component_id) : null,
        psu_label: form.psu_label || null,
        cable_type: form.cable_type || null,
        cable_color: form.cable_color || null,
        notes: form.notes || null,
      }, companyId);
      onChanged?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`¿Desconectar «${consumerName}» de la toma #${outlet.outlet_number}?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      await api.disconnectUpsOutlet(upsId, outlet.id, companyId);
      onChanged?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveProps = async () => {
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      await api.updateUpsOutlet(upsId, outlet.id, {
        label: propsForm.label || null,
        outlet_type: propsForm.outlet_type || null,
        phase: propsForm.phase || null,
        circuit_id: propsForm.circuit_id || null,
        voltage: propsForm.voltage !== '' ? Number(propsForm.voltage) : null,
        amperage: propsForm.amperage !== '' ? Number(propsForm.amperage) : null,
        is_active: propsForm.is_active,
        notes: propsForm.notes || null,
      }, companyId);
      setOkMsg('Propiedades guardadas');
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const groups = useMemo(() => {
    const map = {};
    CONSUMER_GROUPS.forEach(g => { map[g.key] = []; });
    consumers.forEach(c => { map[consumerGroup(c)].push(c); });
    return CONSUMER_GROUPS
      .map(g => ({ ...g, items: map[g.key] || [] }))
      .filter(g => g.items.length > 0);
  }, [consumers]);

  const inputStyle = {
    display: 'block',
    width: '100%',
    marginTop: 3,
    padding: '0.32rem 0.5rem',
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 5,
    fontSize: '0.82rem',
    color: 'var(--text-primary)',
  };
  const labelStyle = {
    fontSize: '0.76rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  };
  const sectionTitle = {
    fontSize: '0.82rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: 6,
    marginTop: 2,
  };
  const btnPrimary = {
    background: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '0.42rem 0.9rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: busy ? 'not-allowed' : 'pointer',
  };
  const btnSecondary = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '0.4rem 0.8rem',
    fontSize: '0.82rem',
    color: 'var(--text-secondary)',
    cursor: busy ? 'not-allowed' : 'pointer',
  };
  const btnDanger = {
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '0.4rem 0.8rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: busy ? 'not-allowed' : 'pointer',
  };

  return (
    <Dialog
      title={`Toma #${outlet.outlet_number} · ${outlet.label || 'Salida del SAI'}`}
      icon="⚡"
      maxWidth={620}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" style={btnSecondary} onClick={onClose} disabled={busy}>
            Cerrar
          </button>
          <button type="button" style={btnPrimary} onClick={handleSaveProps} disabled={busy}>
            Guardar propiedades
          </button>
        </>
      )}
    >
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          color: '#fca5a5',
          border: '1px solid rgba(239,68,68,0.3)',
          padding: '0.4rem 0.6rem',
          borderRadius: 5,
          fontSize: '0.8rem',
          marginBottom: 10,
        }}>
          ⚠ {error}
        </div>
      )}
      {okMsg && (
        <div style={{
          background: 'rgba(34,197,94,0.1)',
          color: '#16a34a',
          border: '1px solid rgba(34,197,94,0.3)',
          padding: '0.4rem 0.6rem',
          borderRadius: 5,
          fontSize: '0.8rem',
          marginBottom: 10,
        }}>
          ✓ {okMsg}
        </div>
      )}

      {occupied ? (
        <div style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>Equipo alimentado</div>
          <div style={{
            background: isPduChild ? 'rgba(245,158,11,0.08)' : 'rgba(74,222,128,0.08)',
            border: `1px solid ${isPduChild ? 'rgba(245,158,11,0.3)' : 'rgba(74,222,128,0.3)'}`,
            borderRadius: 8,
            padding: '0.65rem 0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                {consumerName}
              </strong>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 2 }}>
                {isPduChild
                  ? 'Entrada de la PDU (fuente aguas arriba)'
                  : [connection?.psu_label, connection?.cable_type, connection?.cable_color].filter(Boolean).join(' · ') || 'Equipo conectado'}
              </div>
            </div>
            <button type="button" style={btnDanger} onClick={handleDisconnect} disabled={busy}>
              Desconectar
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>Conectar un equipo a esta toma</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
            <label style={labelStyle}>
              Equipo de destino
              <select
                value={form.consumerKey}
                onChange={e => handleSelectConsumer(e.target.value)}
                style={inputStyle}
              >
                <option value="">— Elegir equipo —</option>
                {groups.map(group => (
                  <optgroup key={group.key} label={group.label}>
                    {group.items.map(c => (
                      <option key={`${c.consumer_type}:${c.id}`} value={`${c.consumer_type}:${c.id}`}>
                        {c.name}{c.sub_label ? ` · ${c.sub_label}` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              Fuente / PSU
              {loadingPsus ? (
                <div style={{ ...inputStyle, color: 'var(--text-muted)' }}>Cargando PSU…</div>
              ) : psuOptions.length > 0 ? (
                <select
                  value={form.psu_component_id}
                  onChange={e => set('psu_component_id', e.target.value)}
                  style={inputStyle}
                >
                  <option value="">— Sin componente PSU —</option>
                  {psuOptions.map(p => (
                    <option key={p.id} value={p.id} disabled={usedPsuIds.has(p.id)}>
                      {p.slot ? `[${p.slot}] ` : ''}
                      {[p.manufacturer, p.model].filter(Boolean).join(' ') || `PSU #${p.id}`}
                      {usedPsuIds.has(p.id) ? ' (ya conectada)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={form.psu_label}
                  onChange={e => set('psu_label', e.target.value)}
                  placeholder="PSU-A, Entrada PDU…"
                  style={inputStyle}
                />
              )}
            </label>
            <label style={labelStyle}>
              Etiqueta de la entrada
              <input
                type="text"
                value={form.psu_label}
                onChange={e => set('psu_label', e.target.value)}
                placeholder="PSU-A, Entrada PDU…"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Tipo de cable
              <select
                value={form.cable_type}
                onChange={e => set('cable_type', e.target.value)}
                style={inputStyle}
              >
                {CABLE_TYPES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              Color del cable
              <select
                value={form.cable_color}
                onChange={e => set('cable_color', e.target.value)}
                style={inputStyle}
              >
                {CABLE_COLORS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              Notas
              <input
                type="text"
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                placeholder="Observaciones de la conexión"
                style={inputStyle}
              />
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button type="button" style={btnPrimary} onClick={handleConnect} disabled={busy}>
              Conectar equipo
            </button>
          </div>
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <div style={sectionTitle}>Propiedades de la toma</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
          <label style={labelStyle}>
            Etiqueta
            <input
              type="text"
              value={propsForm.label}
              onChange={e => setProp('label', e.target.value)}
              placeholder={`Toma ${outlet.outlet_number}`}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Tipo
            <select
              value={propsForm.outlet_type}
              onChange={e => setProp('outlet_type', e.target.value)}
              style={inputStyle}
            >
              {OUTLET_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
          <label style={labelStyle}>
            Fase
            <select
              value={propsForm.phase}
              onChange={e => setProp('phase', e.target.value)}
              style={inputStyle}
            >
              <option value="">—</option>
              <option value="L1">L1</option>
              <option value="L2">L2</option>
              <option value="L3">L3</option>
            </select>
          </label>
          <label style={labelStyle}>
            Circuito
            <input
              type="text"
              value={propsForm.circuit_id}
              onChange={e => setProp('circuit_id', e.target.value)}
              placeholder="C1, A3…"
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Tensión (V)
            <input
              type="number"
              value={propsForm.voltage}
              onChange={e => setProp('voltage', e.target.value)}
              min={0}
              step={1}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Amperaje máx. (A)
            <input
              type="number"
              value={propsForm.amperage}
              onChange={e => setProp('amperage', e.target.value)}
              min={0}
              step={0.5}
              style={inputStyle}
            />
          </label>
        </div>
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          <input
            type="checkbox"
            checked={propsForm.is_active}
            onChange={e => setProp('is_active', e.target.checked)}
          />
          Toma activa
        </label>
      </div>
    </Dialog>
  );
}

// ── Panel principal ──────────────────────────────────────────────────────────

export default function UpsOutletsPanel({
  upsId,
  companyId,
  outletsCount = 0,
  onChanged,
}) {
  const [data, setData] = useState(null);
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const load = useCallback(async () => {
    if (!upsId || !companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getUpsOutlets(upsId, companyId);
      setData(res.data || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [upsId, companyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!upsId || !companyId) return;
    api.getUpsConsumers(upsId, companyId)
      .then(res => setConsumers(res.data || []))
      .catch(() => setConsumers([]));
  }, [upsId, companyId]);

  const outlets = data?.outlets || [];
  const total = data?.outlets_total ?? outlets.length;
  const used = data?.outlets_used ?? outlets.filter(o => o.consumer_kind).length;
  const free = Math.max(total - used, 0);
  const usedPct = total > 0 ? Math.round((used / total) * 100) : 0;

  if (loading && !data) {
    return <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Cargando tomas del SAI…</div>;
  }
  if (error) {
    return <div style={{ color: '#ef4444', fontSize: '0.85rem' }}>⚠ {error}</div>;
  }
  if (!data?.rack_element_id) {
    return (
      <p className="dv-hint">
        Asigna una ubicación o rack al SAI y guarda para generar sus tomas de salida.
      </p>
    );
  }
  if (total === 0) {
    return (
      <p className="dv-hint">
        {outletsCount > 0
          ? 'Las tomas se generarán al guardar el SAI con el nº de tomas indicado.'
          : 'Indica el «Nº de tomas de salida» en la pestaña Potencia y guarda para generarlas.'}
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '1.35rem', fontWeight: 700, color: '#6366f1' }}>{used}</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>ocupadas</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-muted)' }}>{free}</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>libres</span>
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ height: 10, background: 'var(--border)', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{
              width: `${usedPct}%`,
              height: '100%',
              background: usedPct > 80 ? '#ef4444' : usedPct > 60 ? '#f59e0b' : '#22c55e',
              transition: 'width 0.3s',
            }} />
          </div>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{usedPct}% ocupado</span>
        </div>
        <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Pulsa una toma para conectarla o editar sus propiedades
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {[['grid', '⬛ Cuadrícula'], ['diagram', '⚡ Diagrama']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setViewMode(key)}
              style={{
                background: viewMode === key ? 'rgba(99,102,241,0.2)' : 'transparent',
                color: viewMode === key ? '#818cf8' : 'var(--text-secondary)',
                border: 'none',
                padding: '0.25rem 0.75rem',
                fontSize: '0.78rem',
                fontWeight: viewMode === key ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'diagram' ? (
        <ConnectionDiagram outlets={outlets} />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {outlets.map(outlet => (
            <OutletCard key={outlet.id} outlet={outlet} onOpen={setEditing} />
          ))}
        </div>
      )}

      {editing && (
        <OutletDialog
          outlet={editing}
          upsId={upsId}
          companyId={companyId}
          consumers={consumers}
          onClose={() => setEditing(null)}
          onChanged={async () => {
            await load();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}
